// TTSHistoryPanel.jsx
import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Alert,
  Stack,
  Grid,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  TablePagination,
  Menu,
  MenuList,
  MenuItem as MuiMenuItem,
  ListItemAvatar,
  Avatar,
  Skeleton
} from '@mui/material';
import {
  VolumeUp as TTSIcon,
  PlayArrow as PlayIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Article as TextIcon,
  AudioFile as AudioFileIcon,
  CloudDownload as CloudDownloadIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  RecordVoiceOver as VoiceIcon,
  Mood as MoodIcon,
  TextFields as CharacterIcon,
  DateRange as DateIcon,
  Info as InfoIcon
} from '@mui/icons-material';

import { 
  useGetTTSHistoryQuery, 
  useDeleteTTSRequestMutation,
  useGetDownloadTTSQuery 
} from '../../../redux/thunks/ai/gettshistory';

const TTSHistoryPanel = ({ 
  businessId, 
  businessName,
  accountType = 'free' 
}) => {
  const token = useSelector((state) => state.auth?.token);
  
  // State for filters and pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);

  // RTK Query for TTS history
  const { 
    data: historyData, 
    isLoading: historyLoading, 
    error: historyError,
    refetch: refetchHistory 
  } = useGetTTSHistoryQuery({
    authType: 'token',
    token,
    limit: rowsPerPage,
    offset: page * rowsPerPage,
    status: statusFilter === 'all' ? null : statusFilter,
    businessId: businessId,
    sortBy,
    sortOrder
  });

  // Delete mutation
  const [deleteTTSRequest, { isLoading: deleteLoading }] = useDeleteTTSRequestMutation();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleSortChange = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
    setPage(0);
  };

  const handleMenuOpen = (event, request) => {
    setAnchorEl(event.currentTarget);
    setSelectedRequest(request);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRequest(null);
  };

  const handleDeleteClick = (request) => {
    setRequestToDelete(request);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!requestToDelete) return;
    
    try {
      await deleteTTSRequest({
        requestId: requestToDelete.requestId,
        authType: 'token',
        token
      }).unwrap();
      
      setDeleteDialogOpen(false);
      setRequestToDelete(null);
      refetchHistory();
    } catch (err) {
      console.error('Error deleting TTS request:', err);
      // TODO: Show error toast
    }
  };

  const handleDownload = (audioUrl, fileName) => {
    if (!audioUrl) return;
    
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = audioUrl;
    link.download = fileName || 'tts-audio.mp3';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckIcon color="success" />;
      case 'processing': 
      case 'pending': return <ScheduleIcon color="warning" />;
      case 'failed': return <ErrorIcon color="error" />;
      case 'cancelled': return <WarningIcon color="disabled" />;
      default: return <InfoIcon color="disabled" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing':
      case 'pending': return 'warning';
      case 'failed': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Loading skeleton
  if (historyLoading && !historyData) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>TTS Generation History</Typography>
        <Stack spacing={2}>
          {[1, 2, 3, 4, 5].map((item) => (
            <Card key={item} variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" />
                  </Box>
                  <Skeleton variant="rectangular" width={80} height={32} />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Box>
    );
  }

  // Error state
  if (historyError) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>TTS Generation History</Typography>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={refetchHistory}>
            Retry
          </Button>
        }>
          Failed to load TTS history. Please try again.
        </Alert>
      </Box>
    );
  }

  const requests = historyData?.requests || [];
  const pagination = historyData?.pagination || {};
  const summary = historyData?.summary || {};

  return (
    <Box>
      {/* Header with summary stats */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">TTS Generation History</Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={refetchHistory}
          disabled={historyLoading}
          size="small"
          variant="outlined"
        >
          Refresh
        </Button>
      </Box>

      {/* Summary Cards */}
      {summary && Object.keys(summary).length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h6" color="primary">
                  {summary.totalRequests || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Requests
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h6" color="success.main">
                  {summary.completedRequests || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h6" color="secondary">
                  {(summary.totalCharacters || 0).toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Characters
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h6" color="info.main">
                  {formatDuration(summary.totalAudioDuration || 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Audio Duration
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters and Controls */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => handleSortChange(e.target.value)}
              >
                <MenuItem value="createdAt">Date Created</MenuItem>
                <MenuItem value="updatedAt">Last Updated</MenuItem>
                <MenuItem value="characterCount">Character Count</MenuItem>
                <MenuItem value="status">Status</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              startIcon={<SortIcon />}
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              size="small"
            >
              {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            </Button>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              {pagination.total || 0} total requests
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Requests List */}
      {requests.length === 0 ? (
        <Alert severity="info">
          <Typography variant="body2">
            No TTS generations found. Start creating voice content to see your history here.
          </Typography>
        </Alert>
      ) : (
        <Stack spacing={2}>
          {requests.map((request) => (
            <Card key={request.requestId} variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {/* Status Icon and Avatar */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ bgcolor: 'primary.light' }}>
                      <TTSIcon />
                    </Avatar>
                    {getStatusIcon(request.status)}
                  </Box>

                  {/* Main Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Header Row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {request.textPreview}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                          <Chip 
                            label={request.status} 
                            size="small" 
                            color={getStatusColor(request.status)}
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary">
                            <DateIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                            {formatDate(request.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, request)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>

                    {/* Details Grid */}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <VoiceIcon fontSize="small" color="action" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Voice</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {request.voiceName || 'Unknown Voice'}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CharacterIcon fontSize="small" color="action" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Characters</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {request.characterCount.toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      {request.duration && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TimeIcon fontSize="small" color="action" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Duration</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {formatDuration(request.duration)}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {request.fileSize && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AudioFileIcon fontSize="small" color="action" />
                            <Box>
                              <Typography variant="caption" color="text.secondary">File Size</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {formatFileSize(request.fileSize)}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      )}
                    </Grid>

                    {/* Parameters Row */}
                    {request.parameters && (
                      <Box sx={{ mt: 2 }}>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                          {request.parameters.emotionalInstructions && (
                            <Chip 
                              label={`${request.parameters.emotionalInstructions} tone`}
                              size="small" 
                              variant="outlined"
                              icon={<MoodIcon />}
                            />
                          )}
                          <Chip 
                            label={`${request.parameters.outputFormat?.toUpperCase() || 'MP3'} format`}
                            size="small" 
                            variant="outlined"
                            icon={<AudioFileIcon />}
                          />
                          {request.parameters.speed !== 1.0 && (
                            <Chip 
                              label={`${request.parameters.speed}x speed`}
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      </Box>
                    )}

                    {/* Progress for processing requests */}
                    {(request.status === 'processing' || request.status === 'pending') && request.progress && (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="caption" color="text.secondary">
                            {request.progress.message || 'Processing...'}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Error message for failed requests */}
                    {request.status === 'failed' && request.error && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          {request.error}
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Pagination */}
      {pagination.total > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <TablePagination
            component="div"
            count={pagination.total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedRequest?.status === 'completed' && selectedRequest?.audioUrl && (
          <MuiMenuItem 
            onClick={() => {
              handleDownload(
                selectedRequest.audioUrl, 
                `tts_${selectedRequest.requestId}.${selectedRequest.parameters?.outputFormat || 'mp3'}`
              );
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download Audio</ListItemText>
          </MuiMenuItem>
        )}
        
        {selectedRequest?.status === 'completed' && selectedRequest?.audioUrl && (
          <MuiMenuItem 
            onClick={() => {
              // Open audio in new tab for preview
              window.open(selectedRequest.audioUrl, '_blank');
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <PlayIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Preview Audio</ListItemText>
          </MuiMenuItem>
        )}
        
        <Divider />
        
        <MuiMenuItem 
          onClick={() => handleDeleteClick(selectedRequest)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Request</ListItemText>
        </MuiMenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete TTS Request</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this TTS generation request? 
            This will permanently remove the audio file and cannot be undone.
          </Typography>
          {requestToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Request:</strong> {requestToDelete.textPreview}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Created:</strong> {formatDate(requestToDelete.createdAt)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Status:</strong> {requestToDelete.status}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TTSHistoryPanel;